import React from "react";
import { ArrowRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { HeroImageContainer } from "@/components/home/HeroImageContainer";
import { useTranslations } from 'next-intl';

export const HeroSection: React.FC = () => {
  const t = useTranslations('Hero');
  
  // Video optimization with ref for better control
  const videoRef = React.useRef<HTMLVideoElement>(null);
  
  React.useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Optimize video performance
      const handleVisibilityChange = () => {
        if (document.hidden && video) {
          video.pause();
        } else if (video) {
          video.play().catch(e => console.log("Video play failed:", e));
        }
      };
      
      // Reduce quality on mobile
      const handleResize = () => {
        if (video) {
          if (window.innerWidth < 768) {
            video.playbackRate = 0.75;
          } else {
            video.playbackRate = 1;
          }
        }
      };
      
      // Apply initial settings
      handleResize();
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('resize', handleResize);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);
  
  return (
    <section className="relative w-full h-screen bg-[#05092B] overflow-hidden">
      {/* Video Background Container with Performance Optimizations */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 backdrop-blur-[2px] bg-black bg-opacity-40"></div>
        <video 
          ref={videoRef}
          autoPlay 
          muted 
          loop 
          playsInline
          preload="metadata"
          poster="/images/video-poster.jpg"
          className="absolute top-0 left-0 w-full h-full object-cover scale-105"
        >
          <source src="videos/background-video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      
      {/* Additional Glass Morphism Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/10 to-indigo-900/20 z-0"></div>

      {/* Triangular shapes */}
      <div className="absolute left-0 rtl:right-0 rtl:left-auto top-40 md:top-20 w-[100px] md:w-[182px] h-[300px] md:h-[512px] z-0 animate-triangle-float opacity-70">
        <Image
          src="/svg/triangles-hero-container.svg"
          alt="Triangular shapes background"
          width={182}
          height={512}
          className="w-full h-full object-contain"
          priority
        />
      </div>
      
      {/* Hero Content */}
      <div className="relative w-full h-full z-10 pointer-events-none">
        <div className="mx-auto h-full w-full pointer-events-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 h-full items-center gap-6 lg:gap-8">
            
            {/* Content Section */}
            <div className="flex flex-col justify-center items-center lg:items-start text-center lg:text-left rtl:lg:text-right px-4 sm:px-6 md:px-8 lg:px-0 py-6 md:py-8 lg:py-0 order-2 lg:order-1 max-w-full lg:max-w-none lg:pl-[120px] rtl:lg:pr-[120px] rtl:lg:pl-0 xl:pl-[180px] rtl:xl:pr-[180px] rtl:xl:pl-0">
              
              {/* Tagline */}
              <div className="font-h1-up font-[number:var(--h1-up-font-weight)] text-white text-sm sm:text-base md:text-lg lg:text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:500ms] mb-4 md:mb-5 lg:mb-6 drop-shadow-lg">
                {t('tagline')}
              </div>

              {/* Main heading */}
              <h1 className="font-display-d1 font-[number:var(--display-d1-font-weight)] text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-[74px] tracking-[var(--display-d1-letter-spacing)] leading-tight sm:leading-tight md:leading-tight lg:leading-[84px] [font-style:var(--display-d1-font-style)] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms] mb-5 md:mb-6 lg:mb-8 drop-shadow-xl">
                {t('heading').split('\n').map((line: string, index: number) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < t('heading').split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h1>

              {/* Description */}
              <p className="max-w-[95%] sm:max-w-[90%] md:max-w-[80%] lg:max-w-[582px] font-body-m font-[number:var(--body-m-font-weight)] text-white text-base sm:text-lg md:text-xl lg:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-relaxed sm:leading-relaxed md:leading-relaxed lg:leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:700ms] mb-6 md:mb-8 lg:mb-10 drop-shadow-md">
                {t('description')}
              </p>

              {/* CTA Button */}
              <Button 
                asChild
                className="w-fit h-[55px] sm:h-[60px] md:h-[65px] px-[24px] sm:px-[28px] md:px-[32px] py-4 md:py-5 rounded-[5px] bg-brand-gradient hover:opacity-90 hover:scale-105 transition-all duration-300 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:800ms] shadow-lg hover:shadow-xl pointer-events-auto backdrop-blur-sm"
              >
                <a href="/about#solutions">
                  <span className="font-button font-[number:var(--button-font-weight)] text-white text-base sm:text-lg md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)]">
                    {t('cta')}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 ml-2.5 rtl:ml-0 rtl:mr-2.5" />
                </a>
              </Button>
              
            </div>

            {/* Image Section - Hidden on mobile/tablet, visible on desktop only */}
            {/* <div className="hidden lg:block h-full order-1 lg:order-2 pointer-events-auto opacity-80">
              <HeroImageContainer 
                imageData={{
                  src: "/svg/right-section-hero.svg",
                  alt: "Developer coding on laptop with modern interface"
                }}
                animationDelay="900ms"
              />
            </div> */}
            
          </div>
        </div>
      </div>
    </section>
  );
};